import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from './components/Navbar';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import Footer from './components/Footer';
import OrcamentoModal from './components/modalServices/OrcamentoModal';
import { formatCurrencyView } from './utils/currencyUtils';
import usePermissions from './components/hooks/usePermissions';

function Orcamentos() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('orcamentos');
  
  const [orcamentos, setOrcamentos] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrcamento, setSelectedOrcamento] = useState(null);
  const [fonteRecursos, setFonteRecursos] = useState([]);
  // Removido viewMode - mantendo apenas visualização hierárquica

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const totalItems = orcamentos.length;
  const { setPagination } = usePagination10(totalItems);

  // Remover a paginação dos dados para manter a hierarquia intacta
  // const selectedOrcamentos = orcamentos.slice(
  //   setPagination.startIndex,
  //   setPagination.startIndex + setPagination.rowsPerPage
  // );

  // Função para contar itens em uma estrutura hierárquica
  const contarItens = (estrutura, tipo) => {
    let contador = 0;

    if (tipo === 'programa') {
      Object.values(estrutura.acoes).forEach(acao => {
        contador += acao.itens.length;
        Object.values(acao.subacoes).forEach(subacao => {
          contador += subacao.itens.length;
        });
      });
    } else if (tipo === 'acao') {
      contador += estrutura.itens.length;
      Object.values(estrutura.subacoes).forEach(subacao => {
        contador += subacao.itens.length;
      });
    } else if (tipo === 'subacao') {
      contador += estrutura.itens.length;
    }

    return contador;
  };

  // Função para calcular valor total em uma estrutura hierárquica
  const calcularValorTotal = (estrutura, tipo) => {
    let valorTotal = 0;

    if (tipo === 'programa') {
      Object.values(estrutura.acoes).forEach(acao => {
        acao.itens.forEach(item => {
          valorTotal += parseFloat(item.data.valor) || 0;
        });
        Object.values(acao.subacoes).forEach(subacao => {
          subacao.itens.forEach(item => {
            valorTotal += parseFloat(item.data.valor) || 0;
          });
        });
      });
    } else if (tipo === 'acao') {
      estrutura.itens.forEach(item => {
        valorTotal += parseFloat(item.data.valor) || 0;
      });
      Object.values(estrutura.subacoes).forEach(subacao => {
        subacao.itens.forEach(item => {
          valorTotal += parseFloat(item.data.valor) || 0;
        });
      });
    } else if (tipo === 'subacao') {
      estrutura.itens.forEach(item => {
        valorTotal += parseFloat(item.data.valor) || 0;
      });
    }

    return valorTotal;
  };

  // Função para organizar dados em formato hierárquico
  const organizeHierarchicalData = orcamentos => {
    const organized = [];
    const programas = {};

    orcamentos.forEach(orc => {
      const progKey = `${orc.idPrograma}`;
      if (!programas[progKey]) {
        programas[progKey] = {
          type: 'programa',
          data: orc,
          acoes: {},
        };
      }

      const acaoKey = `${orc.idPrograma}-${orc.idAcao}`;
      if (!programas[progKey].acoes[acaoKey]) {
        programas[progKey].acoes[acaoKey] = {
          type: 'acao',
          data: orc,
          subacoes: {},
          itens: [],
        };
      }

      if (orc.idSubAcao) {
        const subacaoKey = `${orc.idPrograma}-${orc.idAcao}-${orc.idSubAcao}`;
        if (!programas[progKey].acoes[acaoKey].subacoes[subacaoKey]) {
          programas[progKey].acoes[acaoKey].subacoes[subacaoKey] = {
            type: 'subacao',
            data: orc,
            itens: [],
          };
        }
        programas[progKey].acoes[acaoKey].subacoes[subacaoKey].itens.push({
          type: 'item',
          data: orc,
        });
      } else {
        programas[progKey].acoes[acaoKey].itens.push({
          type: 'item',
          data: orc,
        });
      }
    });

    // Converter para array linear com ordenação hierárquica
    Object.values(programas)
      .sort((a, b) => a.data.idPrograma - b.data.idPrograma)
      .forEach(programa => {
        organized.push(programa);

        Object.values(programa.acoes)
          .sort((a, b) => a.data.idAcao - b.data.idAcao)
          .forEach(acao => {
            organized.push(acao);

            // Itens diretos da ação (sem subação) - ordenados por descrição
            acao.itens
              .sort((a, b) => {
                const descA = (a.data.descricaoItem || '').toLowerCase();
                const descB = (b.data.descricaoItem || '').toLowerCase();
                return descA.localeCompare(descB);
              })
              .forEach(item => {
                organized.push(item);
              });

            // Subações e seus itens - ordenados alfabeticamente
            Object.values(acao.subacoes)
              .sort((a, b) => a.data.idSubAcao - b.data.idSubAcao)
              .forEach(subacao => {
                organized.push(subacao);
                subacao.itens
                  .sort((a, b) => {
                    const descA = (a.data.descricaoItem || '').toLowerCase();
                    const descB = (b.data.descricaoItem || '').toLowerCase();
                    return descA.localeCompare(descB);
                  })
                  .forEach(item => {
                    organized.push(item);
                  });
              });
          });
      });

    return organized;
  };

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    fetchOrcamentos();
    fetchFonteRecursos();
  }, [atualizar, podeAbrir, carregandoPermissoes]);

  function fetchOrcamentos() {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ORCAMENTO}/all`, {
        params: { cnpjAssociacao: cnpj, ano: anoBase },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setOrcamentos(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter orcamentos:', error);
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function fetchFonteRecursos() {
    axios
      .get(`${API_BASE_URL.FONTE_RECURSO}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => setFonteRecursos(res.data))
      .catch(err => console.error('Erro ao carregar fontes de recurso:', err));
  }

  function handleDelete(orcamento) {
    showConfirmModal('delete', () => handleConfirmDelete(orcamento));
  }

  function handleConfirmDelete(orcamento) {
    // Construir URL com parâmetro idSubAcao quando necessário
    let url = `${API_BASE_URL.ORCAMENTO}/delete/${orcamento.cnpjAssociacao}/${orcamento.ano}/${orcamento.idPrograma}/${orcamento.idAcao}/${orcamento.idElementoDespesa}/${orcamento.idItem}`;

    // Adicionar idSubAcao como query parameter se existir
    if (orcamento.idSubAcao) {
      url += `?idSubAcao=${orcamento.idSubAcao}`;
    }

    axios
      .delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setAtualizar(result);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir orcamento:', error);
        console.error('URL utilizada:', url);
        console.error('Status do erro:', error.response?.status);
        console.error('Dados do erro:', error.response?.data);

        if (error.response?.data) {
          showDefaultModal('error', error.response.data);
        } else if (error.response?.status === 404) {
          showDefaultModal('error', 'Orçamento não encontrado');
        } else if (error.response?.status >= 500) {
          showDefaultModal(
            'error',
            'Erro interno do servidor. Tente novamente mais tarde.'
          );
        } else {
          showDefaultModal(
            'error',
            'Erro ao excluir o registro! Verifique a conexão com o servidor.'
          );
        }
      });
  }

  function handleNewOrcamento() {
    setSelectedOrcamento(null);
    setShowModal(true);
  }

  function handleEditOrcamento(orcamento) {
    setSelectedOrcamento(orcamento);
    setShowModal(true);
  }

  function handleSuccess() {
    setAtualizar([]);
    setSelectedOrcamento(null);
    setShowModal(false);
  }

  function handleCloseModal() {
    setSelectedOrcamento(null);
    setShowModal(false);
  }

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
        <PageCard
          title="Orcamentos Cadastrados"
          icon="bi-cash-coin"
          scrollable={false}
        >
          {!podeAbrir ? (
            <div className="alert alert-warning m-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Acesso Negado</strong>
              <p className="mb-0 mt-2">
                Você não tem permissão para acessar o módulo de Orçamentos.
              </p>
              <p className="mb-0 mt-2">
                <small>Entre em contato com o administrador do sistema.</small>
              </p>
              <button 
                className="btn btn-sm btn-outline-secondary mt-3"
                onClick={() => navigate('/home')}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Voltar
              </button>
            </div>
          ) : (
            <>
          <div className="d-flex justify-content-between align-items-center pt-1 pb-2">
            <div>
              <h4 className="text-secondary text-shadow text-success mb-1">
                ORÇAMENTOS CADASTRADOS
              </h4>
              <small className="text-muted">
                Visualização hierárquica: PROGRAMA → AÇÃO → SUB_AÇÃO → ITEM
              </small>
            </div>
            <div className="d-flex align-items-center">
              <Link
                className="btn btn-secondary btn-sm d-inline-flex align-items-center me-2"
                to="/home"
              >
                <i className="bi bi-house" />
              </Link>
              <button
                onClick={handleNewOrcamento}
                className="btn btn-success btn-sm d-inline-flex align-items-center me-2"
                disabled={!podeCriar}
                title={!podeCriar ? 'Você não tem permissão para criar orçamentos' : 'Novo orçamento'}
              >
                <i className="bi bi-plus-lg" /> Novo
              </button>
            </div>
          </div>

          <div
            className="shadow-lg mb-3 d-none d-md-block"
            style={{
              height: '50vh',
              overflowY: 'auto',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            {/* Versão para telas grandes - apenas hierárquica */}
            <table
              className="table table-hover table-sm small"
              style={{ tableLayout: 'fixed', width: '100%' }}
            >
              <thead className="table-secondary sticky-top bg-light">
                <tr>
                  <th scope="col" style={{ width: '50%' }}>
                    Hierarquia / Orçamento
                  </th>
                  <th scope="col" style={{ width: '25%' }}>
                    Elemento Despesa
                  </th>
                  <th
                    scope="col"
                    style={{ width: '15%' }}
                    className="text-end"
                  >
                    Valor
                  </th>
                  <th
                    scope="col"
                    className="text-center px-2"
                    style={{ width: '10%' }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading &&
                  organizeHierarchicalData(orcamentos).map((item, index) => {
                    const { type, data } = item;
                    let rowClass = '';
                    let description = '';
                    let elementoDespesa = '';
                    let valor = '';
                    let showActions = false;
                    let tooltipText = '';

                    // Calcular contagem de itens baseado na estrutura completa
                    const organizados = (() => {
                      const prog = {};
                      orcamentos.forEach(orc => {
                        const progKey = `${orc.idPrograma}`;
                        if (!prog[progKey]) {
                          prog[progKey] = {
                            type: 'programa',
                            data: orc,
                            acoes: {},
                          };
                        }
                        const acaoKey = `${orc.idPrograma}-${orc.idAcao}`;
                        if (!prog[progKey].acoes[acaoKey]) {
                          prog[progKey].acoes[acaoKey] = {
                            type: 'acao',
                            data: orc,
                            subacoes: {},
                            itens: [],
                          };
                        }
                        if (orc.idSubAcao) {
                          const subacaoKey = `${orc.idPrograma}-${orc.idAcao}-${orc.idSubAcao}`;
                          if (
                            !prog[progKey].acoes[acaoKey].subacoes[subacaoKey]
                          ) {
                            prog[progKey].acoes[acaoKey].subacoes[
                              subacaoKey
                            ] = { type: 'subacao', data: orc, itens: [] };
                          }
                          prog[progKey].acoes[acaoKey].subacoes[
                            subacaoKey
                          ].itens.push({ type: 'item', data: orc });
                        } else {
                          prog[progKey].acoes[acaoKey].itens.push({
                            type: 'item',
                            data: orc,
                          });
                        }
                      });
                      return prog;
                    })();

                    switch (type) {
                      case 'programa':
                        rowClass = 'table-success fw-bold';
                        description = data.descricaoPrograma;
                        const progEstrutura =
                          organizados[`${data.idPrograma}`];
                        if (progEstrutura) {
                          const totalItens = contarItens(
                            progEstrutura,
                            'programa'
                          );
                          const valorTotalProg = calcularValorTotal(
                            progEstrutura,
                            'programa'
                          );
                          tooltipText = `Programa: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalProg);
                        }
                        break;
                      case 'acao':
                        rowClass = 'table-info fw-semibold';
                        description = data.descricaoAcao;
                        const acaoEstrutura =
                          organizados[`${data.idPrograma}`]?.acoes[
                            `${data.idPrograma}-${data.idAcao}`
                          ];
                        if (acaoEstrutura) {
                          const totalItens = contarItens(
                            acaoEstrutura,
                            'acao'
                          );
                          const valorTotalAcao = calcularValorTotal(
                            acaoEstrutura,
                            'acao'
                          );
                          tooltipText = `Ação: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalAcao);
                        }
                        break;
                      case 'subacao':
                        rowClass = 'table-warning fw-semibold';
                        description = data.descricaoSubAcao;
                        const subacaoEstrutura =
                          organizados[`${data.idPrograma}`]?.acoes[
                            `${data.idPrograma}-${data.idAcao}`
                          ]?.subacoes[
                            `${data.idPrograma}-${data.idAcao}-${data.idSubAcao}`
                          ];
                        if (subacaoEstrutura) {
                          const totalItens = contarItens(
                            subacaoEstrutura,
                            'subacao'
                          );
                          const valorTotalSubacao = calcularValorTotal(
                            subacaoEstrutura,
                            'subacao'
                          );
                          tooltipText = `Sub-Ação: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalSubacao);
                        }
                        break;
                      case 'item':
                        rowClass = '';
                        description =
                          data.descricaoItem || 'Item não especificado';
                        elementoDespesa = data.descricaoElementoDespesa;
                        valor = formatCurrencyView(data.valor);
                        showActions = true;
                        tooltipText = `Item: ${description}`;
                        break;
                    }

                    return (
                      <tr
                        key={`${type}-${index}-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                        className={rowClass}
                      >
                        <td
                          style={{
                            width: '50%',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          <div
                            data-bs-toggle="tooltip"
                            data-bs-title={tooltipText}
                          >
                            {description}
                          </div>
                        </td>
                        <td
                          style={{
                            width: '25%',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          <div title={elementoDespesa}>
                            {elementoDespesa || (type !== 'item' ? '-' : '')}
                          </div>
                        </td>
                        <td
                          className="text-end"
                          style={{
                            width: '15%',
                          }}
                        >
                          {valor && (
                            <strong className="text-success">{valor}</strong>
                          )}
                        </td>
                        <td
                          className="text-center"
                          style={{
                            width: '10%',
                          }}
                        >
                          {showActions && (
                            <div className="dropdown">
                              <button
                                className="btn btn-sm p-1"
                                type="button"
                                id={`dropdownMenuButton-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <i className="bi bi-three-dots-vertical" />
                              </button>
                              <ul
                                className="dropdown-menu"
                                aria-labelledby={`dropdownMenuButton-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                              >
                                <li>
                                  <button
                                    onClick={() => handleEditOrcamento(data)}
                                    className="dropdown-item text-primary"
                                    disabled={!podeEditar}
                                    title={!podeEditar ? 'Você não tem permissão para editar' : 'Editar orçamento'}
                                  >
                                    <i className="bi bi-pencil-square me-1" />{' '}
                                    Editar
                                  </button>
                                </li>
                                <li>
                                  <hr className="dropdown-divider border" />
                                </li>
                                <li>
                                  <button
                                    onClick={() => handleDelete(data)}
                                    className="dropdown-item text-danger"
                                    disabled={!podeExcluir}
                                    title={!podeExcluir ? 'Você não tem permissão para excluir' : 'Excluir orçamento'}
                                  >
                                    <i className="bi bi-trash3 me-1" />{' '}
                                    Excluir
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Versão para telas pequenas */}
          <div
            className="table-responsive shadow-lg mb-3 d-block d-md-none"
            style={{ height: '50vh', overflowY: 'auto' }}
          >
            <table className="table table-hover table-sm small">
              <thead className="table-secondary sticky-top bg-light">
                <tr>
                  <th scope="col" style={{ minWidth: '250px' }}>
                    Hierarquia / Orçamento
                  </th>
                  <th scope="col" style={{ minWidth: '200px' }}>
                    Elemento Despesa
                  </th>
                  <th
                    scope="col"
                    style={{ minWidth: '120px' }}
                    className="text-end"
                  >
                    Valor
                  </th>
                  <th
                    scope="col"
                    className="text-center px-2"
                    style={{ minWidth: '80px' }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoading &&
                  organizeHierarchicalData(orcamentos).map((item, index) => {
                    const { type, data } = item;
                    let rowClass = '';
                    let description = '';
                    let elementoDespesa = '';
                    let valor = '';
                    let showActions = false;
                    let tooltipText = '';

                    // Calcular contagem de itens baseado na estrutura completa
                    const organizados = (() => {
                      const prog = {};
                      orcamentos.forEach(orc => {
                        const progKey = `${orc.idPrograma}`;
                        if (!prog[progKey]) {
                          prog[progKey] = {
                            type: 'programa',
                            data: orc,
                            acoes: {},
                          };
                        }
                        const acaoKey = `${orc.idPrograma}-${orc.idAcao}`;
                        if (!prog[progKey].acoes[acaoKey]) {
                          prog[progKey].acoes[acaoKey] = {
                            type: 'acao',
                            data: orc,
                            subacoes: {},
                            itens: [],
                          };
                        }
                        if (orc.idSubAcao) {
                          const subacaoKey = `${orc.idPrograma}-${orc.idAcao}-${orc.idSubAcao}`;
                          if (
                            !prog[progKey].acoes[acaoKey].subacoes[subacaoKey]
                          ) {
                            prog[progKey].acoes[acaoKey].subacoes[
                              subacaoKey
                            ] = { type: 'subacao', data: orc, itens: [] };
                          }
                          prog[progKey].acoes[acaoKey].subacoes[
                            subacaoKey
                          ].itens.push({ type: 'item', data: orc });
                        } else {
                          prog[progKey].acoes[acaoKey].itens.push({
                            type: 'item',
                            data: orc,
                          });
                        }
                      });
                      return prog;
                    })();

                    switch (type) {
                      case 'programa':
                        rowClass = 'table-success fw-bold';
                        description = data.descricaoPrograma;
                        const progEstrutura =
                          organizados[`${data.idPrograma}`];
                        if (progEstrutura) {
                          const totalItens = contarItens(
                            progEstrutura,
                            'programa'
                          );
                          const valorTotalProg = calcularValorTotal(
                            progEstrutura,
                            'programa'
                          );
                          tooltipText = `Programa: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalProg);
                        }
                        break;
                      case 'acao':
                        rowClass = 'table-info fw-semibold';
                        description = data.descricaoAcao;
                        const acaoEstrutura =
                          organizados[`${data.idPrograma}`]?.acoes[
                            `${data.idPrograma}-${data.idAcao}`
                          ];
                        if (acaoEstrutura) {
                          const totalItens = contarItens(
                            acaoEstrutura,
                            'acao'
                          );
                          const valorTotalAcao = calcularValorTotal(
                            acaoEstrutura,
                            'acao'
                          );
                          tooltipText = `Ação: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalAcao);
                        }
                        break;
                      case 'subacao':
                        rowClass = 'table-warning fw-semibold';
                        description = data.descricaoSubAcao;
                        const subacaoEstrutura =
                          organizados[`${data.idPrograma}`]?.acoes[
                            `${data.idPrograma}-${data.idAcao}`
                          ]?.subacoes[
                            `${data.idPrograma}-${data.idAcao}-${data.idSubAcao}`
                          ];
                        if (subacaoEstrutura) {
                          const totalItens = contarItens(
                            subacaoEstrutura,
                            'subacao'
                          );
                          const valorTotalSubacao = calcularValorTotal(
                            subacaoEstrutura,
                            'subacao'
                          );
                          tooltipText = `Sub-Ação: ${description} (${totalItens} ${
                            totalItens === 1 ? 'item' : 'itens'
                          })`;
                          valor = formatCurrencyView(valorTotalSubacao);
                        }
                        break;
                      case 'item':
                        rowClass = '';
                        description =
                          data.descricaoItem || 'Item não especificado';
                        elementoDespesa = data.descricaoElementoDespesa;
                        valor = formatCurrencyView(data.valor);
                        showActions = true;
                        tooltipText = `Item: ${description}`;
                        break;
                    }

                    return (
                      <tr
                        key={`mobile-${type}-${index}-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                        className={rowClass}
                      >
                        <td
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          <div
                            data-bs-toggle="tooltip"
                            data-bs-title={tooltipText}
                          >
                            {description}
                          </div>
                        </td>
                        <td
                          style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          <div title={elementoDespesa}>
                            {elementoDespesa || (type !== 'item' ? '-' : '')}
                          </div>
                        </td>
                        <td className="text-end">
                          {valor && (
                            <strong className="text-success">{valor}</strong>
                          )}
                        </td>
                        <td className="text-center">
                          {showActions && (
                            <div className="dropdown">
                              <button
                                className="btn btn-sm p-1"
                                type="button"
                                id={`dropdownMenuButton-mobile-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <i className="bi bi-three-dots-vertical" />
                              </button>
                              <ul
                                className="dropdown-menu"
                                aria-labelledby={`dropdownMenuButton-mobile-${data.ano}-${data.idPrograma}-${data.idAcao}-${data.idElementoDespesa}-${data.idItem}`}
                              >
                                <li>
                                  <button
                                    onClick={() => handleEditOrcamento(data)}
                                    className="dropdown-item text-primary"
                                    disabled={!podeEditar}
                                    title={!podeEditar ? 'Você não tem permissão para editar' : 'Editar orçamento'}
                                  >
                                    <i className="bi bi-pencil-square me-1" />{' '}
                                    Editar
                                  </button>
                                </li>
                                <li>
                                  <hr className="dropdown-divider border" />
                                </li>
                                <li>
                                  <button
                                    onClick={() => handleDelete(data)}
                                    className="dropdown-item text-danger"
                                    disabled={!podeExcluir}
                                    title={!podeExcluir ? 'Você não tem permissão para excluir' : 'Excluir orçamento'}
                                  >
                                    <i className="bi bi-trash3 me-1" />{' '}
                                    Excluir
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Removido Pagination10 temporariamente para melhor visualização da hierarquia */}
          <div className="text-center small text-muted mt-2">
            Total de orçamentos: {orcamentos.length}
          </div>

          <ConfirmModal {...setConfirmModal} />
          <DefaultModal {...setDefaultModal} />

          <OrcamentoModal
            show={showModal}
            onHide={handleCloseModal}
            orcamento={selectedOrcamento}
            onSuccess={handleSuccess}
            fonteRecursos={fonteRecursos}
          />
          </>
          )}
        </PageCard>
  );
}

export default Orcamentos;
